import { InboxList, INBOX_KINDS } from "@/components/inbox/inbox-list";
import { RefreshButton } from "@/components/list/refresh-button";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STATUS_LABEL } from "@/components/engagements/completion-progress";
import { useInbox, useInboxCount } from "@/hooks/inbox";
import { useTabParam } from "@/hooks/tab-param";
import { cn } from "@/lib/utils";
import type { InboxKind, InboxQuery, InboxStatus } from "@/types/domain";

// Tab order: Prds (my work) first and selected by default, then the
// confirmation queues. No All tab — each tab is its own queue and query.
const KIND_ORDER: InboxKind[] = ["engagement", "rollout", "rc-task", "access-request"];
const STATUSES: InboxStatus[] = ["open", "in_progress"];

// Home ("/") is the Inbox (owner 2026-09-08): every item waiting on me,
// filterable by kind via ?kind= (the retired /inbox, /confirmations and
// /rollout-tasks routes redirect here, keeping the kind). My PRDs moved to
// the /prds tabs.
const Inbox = () => {
  const [filter, setKind] = useTabParam("kind", "engagement");
  const [selectedStatus, setStatus] = useTabParam("status", "open");
  const candidate = filter === "engagement-confirm" || filter === "todo" ? "engagement" : filter;
  // Retired, unknown or the old "all" filter open Prds.
  const kind = KIND_ORDER.find((k) => k === candidate) ?? "engagement";
  const status = STATUSES.find((s) => s === selectedStatus) ?? "open";
  // Each tab queries only what it shows; work items also split by my status.
  const query: InboxQuery = kind === "engagement" ? { kind, status } : { kind };
  const { data: items, isLoading, isValidating, mutate } = useInbox(query);
  const { data: counts, mutate: mutateCounts } = useInboxCount();

  const kindCount = (k: InboxKind) => {
    if (!counts) return "";
    const n =
      k === "engagement"
        ? counts.kinds.engagement.open + counts.kinds.engagement.in_progress
        : counts.kinds[k];
    return ` (${n})`;
  };

  return (
    <div className="space-y-4">
      <PageBreadcrumb current="Inbox" />
      <div className="flex items-center justify-between gap-2">
        {/* Narrow screens: the tabs scroll horizontally, no wrapping */}
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList>
              {KIND_ORDER.map((k) => (
                <TabsTrigger key={k} value={k}>
                  {INBOX_KINDS[k].label}
                  {kindCount(k)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {kind === "engagement" && (
            <Tabs value={status} onValueChange={setStatus}>
              <TabsList aria-label="我的进度">
                {STATUSES.map((s) => (
                  <TabsTrigger key={s} value={s}>
                    {STATUS_LABEL[s]}
                    {counts && ` (${counts.kinds.engagement[s]})`}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </div>
        <RefreshButton
          spinning={isValidating}
          onClick={() => {
            void mutate();
            void mutateCounts();
          }}
        />
      </div>
      <div
        aria-busy={isLoading}
        className={cn(isLoading && items && "opacity-60")}
      >
        <InboxList
          items={items}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default Inbox;
